import React, { useEffect, useState } from "react";
import { useWpMenu } from "../hooks/wp/useWpMenu";
import { useWpProjectsSelection } from "../hooks/wp/useWpProjectsSelection";
import { Link } from "gatsby";
import { AnimatePresence, motion } from "framer-motion";

const HeaderDesktop = ({
  isOpen,
  setIsOpen,
  isProjectsOpen,
  setIsProjectsOpen,
  pathname,
  isSingleProjectPage,
  isFirstTime,
  setIsFirstTime,
  isAuxPage,
  isAuxMenuVisible,
  setIsAuxMenuVisible,
  setWhichHovered,
  auxMenuComponents,
}) => {
  const toggleProjects = () => setIsProjectsOpen(!isProjectsOpen);
  const toggleMenu = () => {
    if (isFirstTime) {
      setIsFirstTime(false);
    }
    setIsOpen(!isOpen);
  };

  const {
    wpMenu: {
      menuItems: { nodes: menu },
    },
  } = useWpMenu();
  const {
    wpPage: {
      projectSelection: {
        selection: { nodes: projects },
      },
    },
  } = useWpProjectsSelection();

  const simplerMenuArray = menu.map((item) => item.connectedNode.node);
  const hasProjectPage = simplerMenuArray.findIndex(
    (item) => item.slug === "projects"
  );
  const filteredMenu =
    hasProjectPage > -1
      ? simplerMenuArray.filter((item) => item.slug !== "projects")
      : simplerMenuArray;

  const [isProjectMenuVisible, setIsProjectMenuVisible] = useState(false);

  useEffect(() => {
    if (!pathname.includes("projects")) {
      setIsProjectsOpen(false);
    } else {
      setIsProjectsOpen(true);
      setIsProjectMenuVisible(false);
    }
  }, [
    pathname,
    setIsProjectsOpen,
    setIsProjectMenuVisible,
    isSingleProjectPage,
  ]);

  const toggleProjectMenu = () =>
    setIsProjectMenuVisible(!isProjectMenuVisible);

  useEffect(() => {
    if (isFirstTime && pathname === "/") {
      setIsOpen(false);
    } else {
      setIsOpen(true);
    }
  }, [isFirstTime, setIsOpen, pathname]);

  useEffect(() => {
    setIsAuxMenuVisible(true);
  }, [pathname, setIsAuxMenuVisible]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
    exit: { opacity: 0 },
  };

  return (
    <motion.header
      className="hidden md:flex flex-wrap px-4 py-3 justify-between font-bold text-lg bg-white"
      onHoverEnd={() => {
        setWhichHovered("default");
        if (isAuxMenuVisible && pathname !== "/" && !isSingleProjectPage) {
          setIsAuxMenuVisible(false);
          setIsProjectsOpen(false);
        }
      }}
    >
      <motion.nav className="inline transition-all duration-300">
        <motion.button
          className="text-left pr-4"
          onClick={() =>
            isFirstTime
              ? toggleMenu()
              : pathname === "/" && isProjectsOpen
              ? toggleProjects()
              : null
          }
          onHoverStart={() => setWhichHovered("default")}
        >
          {pathname !== "/" ? (
            <Link
              to="/"
              className="uppercase hover:opacity-20 transition-all duration-300 text-left"
            >
              Claudio Majorana{isOpen ? "," : ""}
            </Link>
          ) : (
            <span className="uppercase">
              Claudio Majorana{isOpen ? "," : ""}
            </span>
          )}
        </motion.button>

        {isOpen && projects.length > 0 && hasProjectPage > -1 && (
          <>
            {(!isAuxPage || (isAuxPage && isAuxMenuVisible)) && (
              <motion.button
                onClick={() =>
                  isProjectsOpen && isSingleProjectPage
                    ? toggleProjectMenu()
                    : toggleProjects()
                }
                className={`uppercase relative hover:opacity-20 transition-all duration-300 text-left mr-4`}
                onHoverStart={() => setWhichHovered("projects")}
              >
                Projects
                {isProjectsOpen && !isSingleProjectPage && (
                  <hr className="border-[1px] border-black absolute top-[43%] left-0 w-full" />
                )}
                {isSingleProjectPage || isProjectsOpen ? <>:</> : <>,</>}
              </motion.button>
            )}

            <AnimatePresence>
              {isProjectsOpen && (
                <motion.ul
                  className="inline"
                  variants={containerVariants}
                  initial="hidden"
                  animate="visible"
                  exit="hidden"
                >
                  {projects.map((project, i) => {
                    const isCurrent = pathname.includes(project.slug);
                    return (
                      <motion.button
                        key={i}
                        variants={itemVariants}
                        className={`mr-4 relative ${
                          pathname.includes(project.slug)
                            ? ""
                            : "hover:opacity-20"
                        }  text-left ${
                          !isCurrent &&
                          isSingleProjectPage &&
                          !isProjectMenuVisible
                            ? "hidden pointer-events-none"
                            : !isCurrent && !isSingleProjectPage
                            ? "inline pointer-events-auto"
                            : "inline pointer-events-auto"
                        }`}
                        onClick={() => setIsProjectMenuVisible(false)}
                        onHoverStart={() => setWhichHovered(project.slug)}
                      >
                        <Link
                          to={`/projects/${project.slug}`}
                          className={`uppercase`}
                        >
                          {project.title}
                          {isSingleProjectPage && !isProjectMenuVisible
                            ? ""
                            : ","}
                          {pathname.includes(project.slug) && (
                            <hr className="border-[1px] border-black absolute top-[43%] left-0 w-full" />
                          )}
                        </Link>
                      </motion.button>
                    );
                  })}
                </motion.ul>
              )}
            </AnimatePresence>

            {((!isProjectMenuVisible && !isSingleProjectPage) ||
              (isOpen && isProjectMenuVisible)) && (
              <motion.ul className="inline-flex ">
                {filteredMenu.map((item, i) => {
                  const isCurrent = pathname.includes(item.slug);
                  return (
                    <React.Fragment key={i}>
                      {isCurrent && !isAuxMenuVisible ? (
                        <motion.button
                          className={`mr-4 relative uppercase hover:opacity-20 transition-all duration-300 text-left ${
                            isProjectsOpen ? "opacity-20" : "opacity-100"
                          }`}
                          onHoverStart={() => setIsAuxMenuVisible(true)}
                        >
                          <span className="relative">
                            {item.title}
                            {i === filteredMenu.length - 1 ? "." : " ..."}
                            {isCurrent && (
                              <hr className="border-[1px] border-black absolute top-[43%] left-0 w-full" />
                            )}
                          </span>
                        </motion.button>
                      ) : (
                        <motion.button
                          className={`mr-4 relative hover:opacity-20 transition-all duration-300 text-left ${
                            isAuxMenuVisible ? "block" : "hidden"
                          } ${isProjectsOpen ? "opacity-20" : "opacity-100"}`}
                          onHoverStart={() => setWhichHovered(item.slug)}
                        >
                          <Link to={`/${item.slug}/`} className={`uppercase`}>
                            {item.title}
                            {i === filteredMenu.length - 1 ? "." : ","}
                            {isCurrent && (
                              <hr className="border-[1px] border-black absolute top-[43%] left-0 w-full" />
                            )}
                          </Link>
                        </motion.button>
                      )}
                    </React.Fragment>
                  );
                })}
              </motion.ul>
            )}
          </>
        )}
      </motion.nav>
      {isSingleProjectPage && !isProjectMenuVisible && (
        <div className="flex gap-x-4">
          {auxMenuComponents && auxMenuComponents.description && (
            <button
              className="uppercase hover:opacity-20 transition-all duration-300 text-left"
              onClick={() => auxMenuComponents.description((prev) => !prev)}
            >
              About the project {auxMenuComponents.book ? ", " : "."}
            </button>
          )}
          {auxMenuComponents && auxMenuComponents.book && (
            <button className="text-left">
              <Link
                to={`/books/?slug=${auxMenuComponents.book}`}
                className="uppercase hover:opacity-20 transition-all duration-300"
              >
                See the book.
              </Link>
            </button>
          )}
        </div>
      )}
    </motion.header>
  );
};

export default HeaderDesktop;
